import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import TypingPracticePage from './pages/TypingPracticePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SettingsPage from './pages/SettingsPage';
import ProfessionChoicePage from './pages/ProfessionChoicePage';
import ComprehensiveGrammarPage from './pages/ComprehensiveGrammarPage';
import VocabularyPage from './pages/VocabularyPage';
import UnknownWordsPage from './pages/UnknownWordsPage';
import { ProjektLesenOverview, ProjektLesenModulePage, ProjektLesenTeilPage } from './pages/exam/b2/ProjektLesenPage';
import WritingJournalPage from './pages/WritingJournalPage';
import TimedChallengePage from './pages/TimedChallengePage';
import ProgressPage from './pages/ProgressPage';
import BookmarksPage from './pages/BookmarksPage';
import ChatPage from './pages/ChatPage';
import JobApplicationsPage from './pages/JobApplicationsPage';
import { DashboardShell } from './components/Dashboard/DashboardLayout';
import { canUseJobTracker } from './lib/jobTracker';
import PlatformPresence from './components/PlatformPresence';
import GlobalChatNotifications from './components/chat/GlobalChatNotifications';
import AITutorFloatingWidget from './components/chat/AITutorFloatingWidget';
import { LanguageProvider } from './contexts/LanguageContext';
import { Skeleton, AuthSkeleton } from './components/ui/Skeleton';

const RouteSkeleton = () => (
  <div className="min-h-screen bg-[#f7f7f8] flex">
    <div className="hidden md:flex w-64 flex-col border-r border-zinc-200 bg-white p-4 space-y-4">
      <Skeleton className="h-9 w-36 rounded-xl" />
      <div className="space-y-2 pt-4">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-9 w-full rounded-lg" />
        ))}
      </div>
    </div>
    <div className="flex-1 p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-28 rounded-xl border border-zinc-200 bg-white p-5 space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, emailConfirmed, loading } = useAuth();

  if (loading) {
    return <RouteSkeleton />;
  }

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Block dashboard if email is not yet confirmed
  if (!emailConfirmed) {
    return <Navigate to="/login" state={{ needsConfirmation: true }} />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <RouteSkeleton />;
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
// Job Tracker Route Component (restricted to allowed accounts)
const JobTrackerRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <RouteSkeleton />;
  }

  if (!isAuthenticated) return <Navigate to="/login" />;

  if (!canUseJobTracker(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <AuthSkeleton />;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      {/* All dashboard pages share ONE persistent shell (sidebar + topbar),
          so the sidebar looks and behaves the same on every page. */}
      <Route element={<ProtectedRoute><DashboardShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/typing-practice" element={<TypingPracticePage />} />
        <Route path="/grammar" element={<ComprehensiveGrammarPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/profession" element={<ProfessionChoicePage />} />
        <Route path="/vocabulary" element={<VocabularyPage />} />
        <Route path="/unknown-words" element={<UnknownWordsPage />} />
        <Route path="/known-words" element={<UnknownWordsPage />} />
        <Route path="/exam/b2/lesen" element={<ProjektLesenOverview />} />
        <Route path="/exam/b2/lesen/teil/:teil" element={<ProjektLesenModulePage />} />
        <Route path="/exam/b2/lesen/teil/:teil/modul/:test" element={<ProjektLesenTeilPage />} />
        <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
        <Route path="/timed-challenge" element={<TimedChallengePage />} />
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/bookmarks" element={<BookmarksPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/writing-journal" element={<WritingJournalPage />} />
        <Route path="/applications" element={<JobTrackerRoute><JobApplicationsPage /></JobTrackerRoute>} />
      </Route>
      <Route path="/exam/b2" element={<Navigate to="/exam/b2/lesen" replace />} />
      <Route path="/exam/b2/projekt" element={<Navigate to="/exam/b2/lesen" replace />} />
      <Route path="/job-tracker" element={<Navigate to="/applications" replace />} />
      <Route path="/pune" element={<Navigate to="/applications" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <PlatformPresence />
          <GlobalChatNotifications />
          <AITutorFloatingWidget />
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
